import React, { useEffect, useState } from 'react';
import './HRVAnalysis.css';
import axios from 'axios';
import LineChart from './../charts/LineChart.js';
import BoxPlot from '../charts/BoxPlot.js';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import ClipLoader from 'react-spinners/ClipLoader';
import CONFIG from '../config.js';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, Title, Tooltip, Legend);

const HRVAnalysis = ({ user, maxDate }) => {
    const formatDate = (date) => {

        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2)
            month = '0' + month;
        if (day.length < 2)
            day = '0' + day;

        return [year, month, day].join('-');
    }

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const tabNames = ['overview', 'quartile-analysis', 'weekday-weekend-recovery', 'advanced-metrics', 'overview', 'swings'];
    const [selectedOption, setSelectedOption] = useState('weekly');
    const [displayData, setDisplayData] = useState();
    const [weeklyData, setWeeklyData] = useState([null, null, null, null, null, null]);
    const [monthlyData, setMonthlyData] = useState([null, null, null, null, null, null, null]);
    const [quarterlyData, setQuarterlyData] = useState([null, null, null, null, null, null, null]);
    const [nocturnalHRVData, setNocturnalHRVData] = useState([null, null, null, null]);
    const [prevMonthHRVOverview, setPrevMonthHRVOverview] = useState();
    const [prevMonthNocturnalHRV, setPrevMonthNocturnalHRV] = useState();
    const [updatedTimePeriod, setUpdatedTimePeriod] = useState('weekly');
    const [disableFilters, setDisableFilters] = useState(false);
    const [showNoDataMsg, setShowNoDataMsg] = useState(false);
    const [showErrorMsg, setShowErrorMsg] = useState(false);
    let currentDate = new Date(maxDate);
    const weeklyDateSet = [null, formatDate(currentDate)];
    const monthlyDateSet = [null, formatDate(currentDate)];
    const quarterlyDateSet = [null, formatDate(currentDate)];
    const [displayEndDate, setDisplayEndDate] = useState(formatDate(currentDate));
    currentDate.setDate(currentDate.getDate() - 7);
    const [displayStartDate, setDisplayStartDate] = useState(formatDate(currentDate.toDateString()));
    weeklyDateSet[0] = formatDate(currentDate.toDateString());
    currentDate = new Date(maxDate);
    currentDate.setDate(currentDate.getDate() - 30);
    monthlyDateSet[0] = formatDate(currentDate.toDateString());
    currentDate = new Date(maxDate);
    currentDate.setDate(currentDate.getDate() - 90);
    quarterlyDateSet[0] = formatDate(currentDate.toDateString());

    const url = CONFIG.createUrl();

    const config = {headers: CONFIG.HEADERS};

    const fetchHRVDashboardData = async () => {
        let timePeriodIndex = 3;
        const resetCustomRangeHRVData = [...nocturnalHRVData];
        resetCustomRangeHRVData[timePeriodIndex] = null;
        setNocturnalHRVData(resetCustomRangeHRVData);
        setUpdatedTimePeriod(selectedOption);
        let startDate = displayStartDate;
        let endDate = displayEndDate
        if (activeTab === 4) {
            timePeriodIndex = 1;
            if (weeklyData[activeTab]) {
                setDisplayData(weeklyData[activeTab]);
                setTimeout(() => {
                    setLoading(false);
                }, 100);
                return;
            }
            startDate = monthlyDateSet[0];
            endDate = monthlyDateSet[1];
        } else if (selectedOption === 'weekly') {
            timePeriodIndex = 0;
            if (weeklyData[activeTab]) {
                setDisplayData(weeklyData[activeTab]);
                setTimeout(() => {
                    setLoading(false);
                }, 100);
                return;
            }
        } else if (selectedOption === 'monthly') {
            timePeriodIndex = 1;
            if (monthlyData[activeTab]) {
                setDisplayData(monthlyData[activeTab]);
                setTimeout(() => {
                    setLoading(false);
                }, 100);
                return;
            }
        } else if (selectedOption === 'quarterly') {
            timePeriodIndex = 2;
            if (quarterlyData[activeTab]) {
                setDisplayData(quarterlyData[activeTab]);
                setTimeout(() => {
                    setLoading(false);
                }, 100);
                return;
            }
        }
        let metric = 'hrv';
        if (activeTab === 3) {
            metric = 'heart_rate'
        }
        setLoading(true);
        try {
            const response1 = await axios.get(`${url}${metric}/${tabNames[activeTab]}?uuid=${user.uuid}&start_date=${startDate}&end_date=${endDate}`, config);
            if (response1.data.average_hrv === null || response1.data.q25 === null || response1.data.weekdays_top_quartile_percent === null || response1.data.min === null || response1.data.biggest_increase === null) {
                setDisplayData({});
                setShowNoDataMsg(true);
                return;
            } else {
                setShowNoDataMsg(false);
            }
            if (nocturnalHRVData[timePeriodIndex] === null) {
                const nocturnalHRVMetric = 'Nocturnal HRV';
                const response2 = await axios.get(`${url}metric/${nocturnalHRVMetric}/?uuid=${user.uuid}&start_date=${startDate}&end_date=${endDate}`, config);
                const newHRVData = [...nocturnalHRVData];
                newHRVData[timePeriodIndex] = response2.data.data;
                setNocturnalHRVData(newHRVData);
            }
            //fetch previous month data for 30-day trends
            if (activeTab === 4) {
                const currentDate = new Date(maxDate);
                endDate = monthlyDateSet[0];
                currentDate.setDate(currentDate.getDate() - 60);
                startDate = formatDate(currentDate.toDateString());
                const nocturnalHRVMetric = 'Nocturnal HRV';
                const [response3, response4] = await axios.all([
                    axios.get(`${url}${metric}/${tabNames[activeTab]}?uuid=${user.uuid}&start_date=${startDate}&end_date=${endDate}`, config),
                    axios.get(`${url}metric/${nocturnalHRVMetric}/?uuid=${user.uuid}&start_date=${startDate}&end_date=${endDate}`, config)
                ]);
                setPrevMonthHRVOverview(response3.data);
                setPrevMonthNocturnalHRV(response4.data.data);
            }
            setDisplayData(response1.data);
            setUpdatedTimePeriod(selectedOption);
            if (activeTab === 4) {
                let newData = [...weeklyData];
                newData[activeTab] = response1.data;
                setWeeklyData(newData);
                newData = [...monthlyData];
                newData[activeTab] = response1.data;
                setMonthlyData(newData);
                newData = [...quarterlyData];
                newData[activeTab] = response1.data;
                setQuarterlyData(newData);
            } else if (selectedOption === 'weekly') {
                const newData = [...weeklyData];
                newData[activeTab] = response1.data;
                setWeeklyData(newData);
            } else if (selectedOption === 'monthly') {
                const newData = [...monthlyData];
                newData[activeTab] = response1.data;
                setMonthlyData(newData);
            } else {
                const newData = [...quarterlyData];
                newData[activeTab] = response1.data;
                setQuarterlyData(newData);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHRVDashboardData();
    }, [activeTab]);

    useEffect(() => {
        if (selectedOption === 'weekly') {
            setDisplayStartDate(weeklyDateSet[0]);
            setDisplayEndDate(weeklyDateSet[1]);
        } else if (selectedOption === 'monthly') {
            setDisplayStartDate(monthlyDateSet[0]);
            setDisplayEndDate(monthlyDateSet[1]);
        } else if (selectedOption === 'quarterly') {
            setDisplayStartDate(quarterlyDateSet[0]);
            setDisplayEndDate(quarterlyDateSet[1]);
        }
    }, [selectedOption, setDisplayStartDate, setDisplayEndDate]);

    const handleSelectionChange = (event) => {
        setSelectedOption(event.target.value);
    }

    const validateDate = () => {
        return (displayStartDate && displayEndDate && (new Date(displayStartDate) < new Date(displayEndDate)));
    }

    const handleEndDateChange = (event) => {
        setDisplayEndDate(event.target.value);
        setSelectedOption('customRange');
        if (validateDate()) {
            setShowErrorMsg(false);
        }
    }

    const handleStartDateChange = (event) => {
        setDisplayStartDate(event.target.value);
        setSelectedOption('customRange');
        if (validateDate()) {
            setShowErrorMsg(false);
        }
    }

    const generateChart = () => {
        if (validateDate()) {
            setShowErrorMsg(false);
            setLoading(true);
            fetchHRVDashboardData();
        } else {
            setShowErrorMsg(true);
        }
    }

    const updateTab = (selectedTab) => {
        if (validateDate()) {
            setShowErrorMsg(false);
            setLoading(true);
            if (selectedTab === 4) {
                setSelectedOption('monthly');
                setDisableFilters(true);
            } else {
                setDisableFilters(false);
            }
            setActiveTab(selectedTab);
        } else {
            setShowErrorMsg(true);
        }
    }

    const renderTable = (tableData) => (
        <table>
            <thead>
                <tr>
                    <th>Day</th>
                    <th>HRV</th>
                </tr>
            </thead>
            <tbody>
                {tableData.dates.map((date, index) => (
                    <tr key={index}>
                        <td>{date}</td>
                        <td>{tableData.values[index]}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const renderHRVOverviewChart = () => {
        const timePeriodIndex = updatedTimePeriod === 'weekly' ? 0 : updatedTimePeriod === 'monthly' ? 1 : updatedTimePeriod === 'quarterly' ? 2 : 3;
        // Create a constant value of the average HRV
        const avgHRVArray = Array(nocturnalHRVData[timePeriodIndex].nocturnal_hrv.date.length).fill(displayData.average_hrv);
        const chartData = {
            labels: nocturnalHRVData[timePeriodIndex].nocturnal_hrv.date,
            datasets: [
                {
                    label: 'HRV',
                    data: nocturnalHRVData[timePeriodIndex].nocturnal_hrv.values,
                    borderColor: 'rgba(66, 107, 179, 1)',
                    backgroundColor: 'rgba(66, 107, 179, 0.2)',
                    borderWidth: 2,
                    pointRadius: 1,
                },
                {
                    label: 'Average HRV',
                    data: avgHRVArray,
                    borderColor: 'rgba(255, 0, 0, 1)',
                    backgroundColor: 'rgba(255, 0, 0, 0.2)',
                    borderWidth: 2,
                    borderDash: [5, 5],
                    pointRadius: 0,
                }
            ],
            chartTitle: `HRV Over the  ${updatedTimePeriod === 'weekly' ? 'Week' : updatedTimePeriod === 'monthly' ? 'Month' : updatedTimePeriod === 'quarterly' ? 'Quarter' : 'Period'}`,
            yAxisLabel: 'HRV (ms)'
        }

        return <LineChart chartData={chartData} />;
    }

    const renderweekDayWeekendRecoveryChart = () => {
        const chartData = {
            labels: ['Weekdays', 'Weekends'],
            datasets: [
                {
                    label: 'Percentage',
                    data: [displayData.weekdays_top_quartile_percent, displayData.weekends_top_quartile_percent],
                    backgroundColor: ['rgba(75, 192, 192, 0.8)', 'rgba(66, 107, 179, 0.8)'],
                    borderColor: ['rgba(75, 192, 192, 1)', 'rgba(66, 107, 179, 1)'],
                    borderWidth: 1,
                    barPercentage: 0.5,
                },
            ],
        };

        const chartOptions = {
            responsive: true,
            plugins: {
                legend: {
                    display: false
                },
                title: {
                    display: true,
                    text: 'HRV in Top 25%: Weekday vs Weekend',
                },
                datalabels: {
                    display: false,
                }
            },
            scales: {
                x: {
                    type: 'category',
                    grid: {
                        display: false,
                    },
                },
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: 'Percentage of Days in Top 25%',
                    },
                    grid: {
                        display: false,
                    },
                }
            },
        };

        return <Bar id={`weekdayWeekendBarChart-${updatedTimePeriod}`
        } data={chartData} options={chartOptions} />
    }

    const renderMonthlyTrendsData = (prevMonthData, hrvData) => {
        const barChartValues = prevMonthData;
        // Calculate number of bins using Sturges' formula
        const numBins = Math.ceil(1 + 3.322 * Math.log10(barChartValues.length));

        // Determine the range of the data
        const minValue = Math.min(...barChartValues);
        const maxValue = Math.max(...barChartValues);

        const curMonthMinValue = Math.min(...hrvData);

        // Calculate the bin width
        const binWidth = Math.ceil((maxValue - minValue) / numBins);

        // Create bins and initialize frequencies
        const bins = Array.from({ length: numBins }, (_, i) => minValue + i * binWidth);
        const prevMonthFrequencies = Array(numBins).fill(0);
        const curMonthFrequencies = Array(numBins).fill(0);

        // Count frequency for each bin
        barChartValues.forEach((value) => {
            const binIndex = Math.min(Math.floor((value - minValue) / binWidth), numBins - 1);
            prevMonthFrequencies[binIndex]++;
        });

        hrvData.forEach((value) => {
            const binIndex = Math.min(Math.floor((value - curMonthMinValue) / binWidth), numBins - 1);
            curMonthFrequencies[binIndex]++;
        });

        const data = {
            labels: bins.map((bin) => bin.toFixed(0)),
            datasets: [
                {
                    label: "Previous Month HRV",
                    data: prevMonthFrequencies.map((val) => val * -1),
                    backgroundColor: "rgba(75, 192, 192, 0.6)",
                    barPercentage: 0.5,
                },
                {
                    label: "Current Month HRV",
                    data: curMonthFrequencies,
                    backgroundColor: "rgba(153, 102, 255, 0.6)",
                    barPercentage: 0.5,
                },
            ],
        };

        const options = {
            indexAxis: 'y',
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: {
                        callback: function (value) {
                            return Math.abs(value);
                        }
                    },
                    title: {
                        display: true,
                        text: 'Frequency'
                    }
                },
                y: {
                    stacked: true,
                    title: {
                        display: true,
                        text: 'HRV (ms)'
                    },
                    reverse: true,
                }
            },
            plugins: {
                legend: {
                    display: true,
                    position: "top",
                },
                datalabels: {
                    display: true,
                    formatter: function (value) {
                        return Math.abs(value);
                    }
                },
                tooltip: {
                    enabled: false
                },
                title: {
                    display: true,
                    text: `HRV Distribution`,
                },
            },
        };

        return <Bar data={data} options={options} />;
    }

    const renderHRVSwingsChart = () => {
        const data = {
            labels: displayData.hrv_data.dates,
            datasets: [
                {
                    type: 'bar', // Bar dataset
                    label: 'Heart Rate Change',
                    data: displayData.hrv_change_data.values.map((val) => val === null ? 0 : val),
                    backgroundColor: function (context) {
                        if (context.raw > 0)
                            return 'rgb(60, 179, 113)';
                        else return 'rgb(227, 50, 81)';
                    }
                },
            ],
        };

        const options = {
            responsive: true,
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        generateLabels: () => [
                            {
                                text: 'Positive Swings',
                                fillStyle: 'rgb(60, 179, 113)',
                            },
                            {
                                text: 'Negative Swings',
                                fillStyle: 'rgb(227, 50, 81)',
                            },
                        ],
                    },
                },
                title: {
                    display: true,
                    text: `HRV and Daily Swings Over the ${updatedTimePeriod === 'weekly' ? 'Week' : updatedTimePeriod === 'monthly' ? 'Month' : updatedTimePeriod === 'quarterly' ? 'Quarter' : 'Period'}`,
                },
                datalabels: {
                    display: false
                }
            },
            scales: {
                y: {
                    title: {
                        display: true,
                        text: 'Heart Rate Change/HRV (ms)'
                    }
                }
            }
        };

        return <Bar data={data} options={options} />;
    }

    const renderBarChart = (chartData, timePeriod, chartName) => {
        const timePeriodIndex = timePeriod === 'weekly' ? 0 : timePeriod === 'monthly' ? 1 : timePeriod === 'quarterly' ? 2 : 3;
        const dataPeriod = timePeriod === 'weekly' ? 'Week' : timePeriod === 'monthly' ? 'Month' : timePeriod === 'quarterly' ? 'Quarter' : 'Period';
        let barChartValues = [];
        let chartTitle = '';
        if (chartName === 'quartileAnalysis') {
            barChartValues = chartData[timePeriodIndex].nocturnal_hrv.values;
            chartTitle = 'Nocturnal HRV';
        } else if (chartName === 'advancedMetrics') {
            barChartValues = chartData;
            chartTitle = 'Heart Rate';
        }

        // Calculate number of bins using Sturges' formula
        const numBins = Math.ceil(1 + 3.322 * Math.log10(barChartValues.length));

        // Determine the range of the data
        const minValue = Math.min(...barChartValues);
        const maxValue = Math.max(...barChartValues);

        // Calculate the bin width
        const binWidth = Math.ceil((maxValue - minValue) / numBins);

        // Create bins and initialize frequencies
        const bins = Array.from({ length: numBins }, (_, i) => minValue + i * binWidth);
        const frequencies = Array(numBins).fill(0);

        // Count frequency for each bin
        barChartValues.forEach((value) => {
            const binIndex = Math.min(Math.floor((value - minValue) / binWidth), numBins - 1);
            frequencies[binIndex]++;
        });

        const barChartData = {
            labels: bins.map((bin) => bin.toFixed(0)),
            datasets: [
                {
                    label: 'Frequency',
                    data: frequencies,
                    backgroundColor: 'rgba(66, 107, 179, 0.8)',
                    borderColor: 'rgba(66, 107, 179, 1)',
                    borderWidth: 1,
                    barPercentage: 1.0,
                    categoryPercentage: 1.0,
                }
            ],
        };

        const barChartOptions = {
            responsive: true,
            plugins: {
                legend: {
                    position: 'top',
                },
                title: {
                    display: true,
                    text: `HRV Distribution for the ${dataPeriod}`,
                },
                datalabels: {
                    display: false,
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: chartTitle,
                    },
                    grid: {
                        display: false,
                    },
                },
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: 'Frequency',
                    },
                    grid: {
                        display: false,
                    }
                },
            },
        };

        return <Bar data={barChartData} options={barChartOptions} />;
    }

    return (
        <div className="container">
            <div className="main-content">
                <section className="card">
                    <h2 className="page-title">HRV Dashboard</h2>
                    <div className="text-container chart-description">
                        <div className="filter-container">
                            <div>
                                <label className="input-label" htmlFor="timePeriodDropdown">Select Time Period:</label>
                                <select className="select-dropdown" name="timePeriodDropdown" id="timePeriodDropdown" disabled={disableFilters} value={selectedOption} onChange={handleSelectionChange}>
                                    <option value="">Select</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="customRange">Custom Range</option>
                                </select>
                            </div>
                            <div>
                                <label className="input-label" htmlFor="startDate">Start Date:</label>
                                <input className="date-input" type="date" id="startDate" name="startDate" disabled={disableFilters} max={maxDate} value={displayStartDate} onChange={handleStartDateChange}></input>
                            </div>
                            <div>
                                <label className="input-label" htmlFor="endDate">End Date:</label>
                                <input className="date-input" type="date" id="endDate" name="endDate" disabled={disableFilters} max={maxDate} value={displayEndDate} onChange={handleEndDateChange}></input>
                            </div>
                            <div style={{ position: 'relative' }}>
                                <button className="generate-button" disabled={disableFilters} onClick={generateChart}>Generate</button>
                                {showErrorMsg &&
                                    <div className="error-msg">Enter valid dates to generate results.</div>
                                }
                            </div>
                        </div>
                        <div>
                            <div className="tabs">
                                <button
                                    className={activeTab === 0 ? 'active' : ''}
                                    onClick={() => updateTab(0)}
                                >
                                    HRV Overview
                                </button>
                                <button
                                    className={activeTab === 1 ? 'active' : ''}
                                    onClick={() => updateTab(1)}
                                >
                                    HRV Quartile Analysis
                                </button>
                                <button
                                    className={activeTab === 2 ? 'active' : ''}
                                    onClick={() => updateTab(2)}
                                >
                                    Weekday vs Weekend Recovery
                                </button>
                                <button
                                    className={activeTab === 3 ? 'active' : ''}
                                    onClick={() => updateTab(3)}
                                >
                                    Advanced HRV and Heart Rate Metrics
                                </button>
                                <button
                                    className={activeTab === 4 ? 'active' : ''}
                                    onClick={() => updateTab(4)}
                                >
                                    Monthly Trends (Last 30 Days)
                                </button>
                                <button
                                    className={activeTab === 5 ? 'active' : ''}
                                    onClick={() => updateTab(5)}
                                >
                                    HRV Swings
                                </button>
                            </div>
                            {loading &&
                                <div className="spinner-container">
                                    <ClipLoader color={"#123abc"} loading={loading} size={50} />
                                </div>
                            }
                            {!loading && showNoDataMsg &&
                                <div className="tab-content no-data-text">
                                    No data recorded in this period.
                                </div>
                            }
                            {!loading && !showNoDataMsg && displayData &&
                                <div className="tab-content">
                                    {activeTab === 0 &&
                                        <div>
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Metric</th>
                                                        <th>Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Average HRV</td>
                                                        <td>{displayData.average_hrv.toFixed(2)} ms</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Peak HRV</td>
                                                        <td>{`${displayData.peak_hrv.value}ms on ${displayData.peak_hrv.date}`}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Lowest HRV</td>
                                                        <td>{`${displayData.lowest_hrv.value}ms on ${displayData.lowest_hrv.date}`}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>HRV Variability</td>
                                                        <td>{displayData.hrv_variability.toFixed(2)} ms</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Percentage of Days Above Baseline HRV</td>
                                                        <td>{displayData.percent_above_baseline.toFixed(0)}%</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Percentage of Days Below Baseline HRV</td>
                                                        <td>{displayData.percent_below_baseline.toFixed(0)}%</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            {renderHRVOverviewChart()}
                                        </div>
                                    }
                                    {activeTab === 1 &&
                                        <div>
                                            <div className="quartile-analysis">
                                                <div className="table-container">
                                                    <h4>Top 25%</h4>
                                                    <div><b>HRV Range: {displayData.q25.toFixed(2)} and above</b></div>
                                                    {renderTable(displayData.top_quartile)}
                                                </div>
                                                <div className="table-container">
                                                    <h4>Bottom 25%</h4>
                                                    <div><b>HRV Range: {displayData.q75.toFixed(2)} and below</b></div>
                                                    {renderTable(displayData.bottom_quartile)}
                                                </div>
                                            </div>
                                            {renderBarChart(nocturnalHRVData, updatedTimePeriod, 'quartileAnalysis')}
                                            {/* <BoxPlot displayData={displayData} hrvData={nocturnalHRVData} chartId={`quartileAnalysis-${selectedOption}`} timePeriod={selectedOption} chartName="quartileAnalysis" /> */}
                                        </div>
                                    }
                                    {activeTab === 2 &&
                                        <div>
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Day Type</th>
                                                        <th>Percentage in Top 25% HRV</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Weekdays</td>
                                                        <td>{displayData.weekdays_top_quartile_percent.toFixed(2)}%</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Weekends</td>
                                                        <td>{displayData.weekends_top_quartile_percent.toFixed(2)}%</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            {renderweekDayWeekendRecoveryChart()}
                                        </div>
                                    }
                                    {activeTab === 3 &&
                                        <div>
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Metric</th>
                                                        <th>Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Mean HR</td>
                                                        <td>{displayData.mean.toFixed(2)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Max HR</td>
                                                        <td>{displayData.max.toFixed(2)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Min HR</td>
                                                        <td>{displayData.min.toFixed(2)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            {/* {renderBarChart(displayData.data.values, selectedOption, 'advancedMetrics')} */}
                                            <BoxPlot displayData={displayData} hrvData={displayData.data.values} chartId={`advancedMetrics-${updatedTimePeriod}`} timePeriod={updatedTimePeriod} chartName="advancedMetrics" />
                                        </div>
                                    }
                                    {activeTab === 4 &&
                                        <div>
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Metric</th>
                                                        <th>Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Previous 30-Day Average HRV</td>
                                                        <td>{displayData.average_hrv.toFixed(2)} ms</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Current Period Average HRV</td>
                                                        <td>{prevMonthHRVOverview.average_hrv.toFixed(2)} ms</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Difference in Average HRV</td>
                                                        <td>{`${Math.abs(displayData.average_hrv - prevMonthHRVOverview.average_hrv).toFixed(2)}`} ms</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Previous 30-Day HRV Variability</td>
                                                        <td>{prevMonthHRVOverview.hrv_variability.toFixed(2)} ms</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Current Period HRV Variability</td>
                                                        <td>{displayData.hrv_variability.toFixed(2)} ms</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            {renderMonthlyTrendsData(prevMonthNocturnalHRV.nocturnal_hrv.values, nocturnalHRVData[1].nocturnal_hrv.values)}
                                            {/* <BoxPlot displayData={prevMonthNocturnalHRV} hrvData={nocturnalHRVData} chartId={`monthlyTrends-${selectedOption}`} timePeriod={selectedOption} chartName="monthlyTrends" /> */}
                                        </div>
                                    }
                                    {activeTab === 5 &&
                                        <div>
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Metric</th>
                                                        <th>Value</th>
                                                        <th>Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Biggest Increase in HRV</td>
                                                        <td>{displayData.biggest_increase.value}</td>
                                                        <td>{displayData.biggest_increase.date}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Biggest Decrease in HRV</td>
                                                        <td>{displayData.biggest_decrease.value}</td>
                                                        <td>{displayData.biggest_decrease.date}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            {renderHRVSwingsChart()}
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default HRVAnalysis;